import { Dropdown } from '@heroui/react'
import { Check, ChevronDown, GraduationCap, Users } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import type { PersonaType } from '../../types/domain'

export function PersonaSwitcher() {
  const { activePersona, canSwitchPersona, switchPersona } = useAuth()

  if (!canSwitchPersona) {
    return null
  }

  const isStaff = activePersona === 'STAFF'

  const handleSelect = (key: string | number) => {
    const next = String(key) as PersonaType
    if (next === 'STAFF' || next === 'PARENT') {
      switchPersona(next)
    }
  }

  return (
    <Dropdown>
      <Dropdown.Trigger
        aria-label="Ganti mode peran aktif"
        data-testid="shell-persona-switcher-btn"
        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition focus-visible:outline-2 focus-visible:outline-wicend-500 focus-visible:outline-offset-2 ${
          isStaff
            ? 'border-teal-200 bg-teal-50/90 text-teal-800 hover:bg-teal-100/90 dark:border-teal-800/80 dark:bg-teal-950/50 dark:text-teal-200 dark:hover:bg-teal-900/60'
            : 'border-emerald-200 bg-emerald-50/90 text-emerald-800 hover:bg-emerald-100/90 dark:border-emerald-800/80 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-900/60'
        }`}
      >
        {isStaff ? (
          <GraduationCap size={15} className="shrink-0 text-teal-600 dark:text-teal-400" />
        ) : (
          <Users size={15} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
        )}
        <span className="hidden sm:inline">
          {isStaff ? 'Guru / Staf' : 'Orang Tua'}
        </span>
        <ChevronDown size={13} className="shrink-0 opacity-70" aria-hidden />
      </Dropdown.Trigger>

      <Dropdown.Popover placement="bottom end" className="w-64">
        <div className="border-b border-slate-200 px-3.5 py-2.5 dark:border-slate-800">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Pilih Mode Peran
          </p>
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            Beralih konteks kerja sekolah atau ruang keluarga
          </p>
        </div>

        <Dropdown.Menu
          aria-label="Daftar mode peran"
          selectedKeys={new Set([activePersona])}
          selectionMode="single"
          onAction={handleSelect}
        >
          <Dropdown.Item
            id="STAFF"
            data-testid="shell-persona-staff-item"
            textValue="Mode Guru / Staf"
            className="py-2.5"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300">
                  <GraduationCap size={17} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    Guru / Staf
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">
                    Operasional sekolah & ERP
                  </p>
                </div>
              </div>
              {isStaff && <Check size={16} className="text-teal-600 dark:text-teal-400" />}
            </div>
          </Dropdown.Item>

          <Dropdown.Item
            id="PARENT"
            data-testid="shell-persona-parent-item"
            textValue="Mode Orang Tua"
            className="py-2.5"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                  <Users size={17} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    Orang Tua
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">
                    Portal SPMB & data anak
                  </p>
                </div>
              </div>
              {!isStaff && <Check size={16} className="text-emerald-600 dark:text-emerald-400" />}
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}
